import type { BodyUUID, Body } from "../types/scene";

export type BodyMap = Record<BodyUUID, Body>;

export function buildBodyParentMap(bodies: BodyMap): Record<BodyUUID, BodyUUID | null> {
  const parentMap: Record<BodyUUID, BodyUUID | null> = {};
  Object.values(bodies || {}).forEach((body) => {
    if (!body || !Array.isArray(body.children)) return;
    body.children.forEach((childUuid) => {
      if (typeof childUuid === "string") {
        parentMap[childUuid as BodyUUID] = body.uuid;
      }
    });
    parentMap[body.uuid] = parentMap[body.uuid] ?? null;
  });
  return parentMap;
}

export function isBodyAncestor(
  bodies: BodyMap,
  ancestorUuid: BodyUUID | string | undefined,
  descendantUuid: BodyUUID | string | undefined
): boolean {
  if (!ancestorUuid || !descendantUuid || ancestorUuid === descendantUuid) return false;
  const parentMap = buildBodyParentMap(bodies);
  let current = parentMap[descendantUuid as BodyUUID] || null;
  while (current) {
    if (current === ancestorUuid) return true;
    current = parentMap[current as BodyUUID] || null;
  }
  return false;
}

export function areBodiesInSameKinematicChain(
  bodies: BodyMap,
  body1Uuid: BodyUUID | string | undefined,
  body2Uuid: BodyUUID | string | undefined
): boolean {
  if (!body1Uuid || !body2Uuid || body1Uuid === body2Uuid) return false;
  return (
    isBodyAncestor(bodies, body1Uuid, body2Uuid) || isBodyAncestor(bodies, body2Uuid, body1Uuid)
  );
}

export function buildBodyRootMap(
  bodies: BodyMap,
  worldBodies: (BodyUUID | { uuid: BodyUUID; rootBodyUuid?: BodyUUID })[]
): Record<BodyUUID, BodyUUID> {
  const parentMap = buildBodyParentMap(bodies);
  const roots: Record<string, BodyUUID> = {};
  const bodyLookup = bodies as Record<string, Body | undefined>;
  const rootCandidates = (worldBodies || [])
    .map((entry) => (typeof entry === "string" ? entry : entry.rootBodyUuid || entry.uuid))
    .filter((uuid): uuid is BodyUUID => typeof uuid === "string");

  const traverse = (bodyUuid: BodyUUID, rootUuid: BodyUUID) => {
    if (!bodyUuid || roots[bodyUuid]) return;
    roots[bodyUuid] = rootUuid;
    const body = bodyLookup[bodyUuid];
    if (!body || !Array.isArray(body.children)) return;
    body.children.forEach((childUuid) => {
      if (typeof childUuid === "string") {
        traverse(childUuid, rootUuid);
      }
    });
  };

  rootCandidates.forEach((root) => traverse(root, root));
  Object.keys(parentMap).forEach((bodyUuid) => {
    if (!roots[bodyUuid]) {
      let current: BodyUUID | null = bodyUuid as BodyUUID;
      while (current && parentMap[current] != null) {
        current = parentMap[current] as BodyUUID;
      }
      if (current) {
        traverse(bodyUuid as BodyUUID, current);
      }
    }
  });

  return roots as Record<BodyUUID, BodyUUID>;
}

export function findBodyForJointUuid(bodies: BodyMap, jointUuid: string): BodyUUID | null {
  for (const body of Object.values(bodies || {})) {
    if (!body || !Array.isArray(body.joints)) continue;
    if (body.joints.some((j) => typeof j === "string" && j === jointUuid)) {
      return body.uuid;
    }
  }
  return null;
}
